import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import type { ChartOptions } from "chart.js";
import { Line } from "react-chartjs-2";
import type { EpicLeaderboardTrendPoint } from "../../types/epicLeaderboard";
import { getEpicColor } from "../../utils/epicColors";
import { formatSeconds } from "./format";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DAY_MS = 24 * 60 * 60 * 1000;

const options: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: true,
  scales: {
    x: {
      ticks: { color: "rgb(199, 238, 255)" },
      grid: { color: "rgb(70, 130, 180)" }
    },
    y: {
      // Lower is better, so don't force the axis down to zero - the
      // interesting movement is a few seconds either way.
      ticks: {
        color: "rgb(199, 238, 255)",
        callback: (value) => formatSeconds(Number(value))
      },
      grid: { color: "rgb(70, 130, 180)" }
    }
  },
  plugins: {
    legend: { display: true, labels: { color: "rgb(199, 238, 255)" } },
    tooltip: {
      callbacks: {
        label: (ctx) =>
          `${ctx.dataset.label}: ${
            ctx.parsed.y === null ? "-" : formatSeconds(ctx.parsed.y)
          }`
      }
    }
  }
};

type Props = {
  epicName: string;
  trend: EpicLeaderboardTrendPoint[];
};

const EpicTrendChart = ({ epicName, trend }: Props) => {
  const color = getEpicColor(epicName);

  // The API switches from weekly to monthly buckets on long ranges; label
  // accordingly so a monthly point doesn't read as a specific day.
  const isMonthly =
    trend.length > 1 &&
    (new Date(trend[1].periodStart).getTime() -
      new Date(trend[0].periodStart).getTime()) /
      DAY_MS >
      20;
  const labels = trend.map((t) =>
    new Date(t.periodStart).toLocaleDateString(
      "en-US",
      isMonthly
        ? { timeZone: "UTC", month: "short", year: "numeric" }
        : { timeZone: "UTC", month: "short", day: "numeric" }
    )
  );

  return (
    <Line
      options={options}
      data={{
        labels,
        datasets: [
          {
            label: "Best",
            data: trend.map((t) => t.bestSeconds),
            borderColor: color,
            backgroundColor: color,
            tension: 0.2,
            spanGaps: true
          },
          {
            label: "Average",
            data: trend.map((t) => t.averageSeconds),
            borderColor: "rgb(199, 238, 255)",
            backgroundColor: "rgb(199, 238, 255)",
            borderDash: [6, 4],
            tension: 0.2,
            spanGaps: true
          }
        ]
      }}
    />
  );
};

export default EpicTrendChart;
